import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
  // While the fallback is showing, a change to any value here clears the error
  // and re-renders children. Lets navigation recover a tripped boundary without
  // remounting (and so resetting) the subtree on every change, unlike a `key`.
  resetKeys?: readonly unknown[];
}

interface ErrorBoundaryState {
  hasError: boolean;
}

const resetKeysChanged = (prev: readonly unknown[] = [], next: readonly unknown[] = []): boolean =>
  prev.length !== next.length || prev.some((value, index) => !Object.is(value, next[index]));

/**
 * Catches render/lifecycle errors in its subtree and shows the fallback
 * instead of unmounting (white-screening) the entire app.
 *
 * Pass `resetKeys` (e.g. the current location) to clear a tripped boundary on
 * navigation without remounting the subtree.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.setState({ hasError: false });
    }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Surface the error for debugging; once the fallback renders the failure
    // would otherwise be invisible
    console.error('Uncaught render error:', error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Rendered outside every provider (theme, router), so it must not depend on
// MUI, styled-components, or navigation — plain elements and inline styles
// only
export const RootErrorFallback = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: 24,
      gap: 8,
      textAlign: 'center',
      fontFamily: 'sans-serif',
    }}
  >
    <h2>Something went wrong</h2>
    <p>An unexpected error occurred. Please reload the page.</p>
    <button type='button' onClick={() => window.location.reload()} style={{ padding: '8px 20px' }}>
      Reload
    </button>
  </div>
);

export default ErrorBoundary;
